// src/pages/ClassificationPage.jsx

import React, { useRef, useState, useEffect, useCallback } from "react";
import {
    Container, Box, Typography, Button, Paper, Stack, CircularProgress, Alert,
    Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Divider,
    Card, CardContent, IconButton, Fade, Slide, Zoom, LinearProgress, Badge,
    Tooltip, Grid, Collapse, Modal, Backdrop
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { classifyImage, submitClassificationFeedback } from "../api/classify";
import { getProfile, getUserHistory } from "../api/user"; // Add this import
import { useAuth } from "../contexts/AuthContext";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CelebrationIcon from "@mui/icons-material/Celebration";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import StarIcon from "@mui/icons-material/Star";
import RecyclingIcon from "@mui/icons-material/Recycling";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import HistoryIcon from "@mui/icons-material/History";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import SpeedIcon from "@mui/icons-material/Speed";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import compostImg from "../assets/bins/compost.png";
import recycleImg from "../assets/bins/recycle.png";
import hazardousImg from "../assets/bins/hazardous.png";
import generalWasteImg from "../assets/bins/general_waste.png";
import unknownImg from "../assets/bins/unknown.png";
import FeedbackDialog from "../components/Feedback/FeedbackDialog"; // Add this import
import ImpactStats from '../components/layout/Footer/ImpactStats';
import TipDisplay from '../components/edutips/TipDisplay';

const BIN_IMAGES = {
    Compost: compostImg,
    Recycle: recycleImg,
    Hazardous: hazardousImg,
    General_Waste: generalWasteImg,
    "Unknown / Still learning": unknownImg,
    Unknown: unknownImg
};

const BIN_LABELS = {
    Compost: "Compost Bin",
    Recycle: "Recycle Bin",
    Hazardous: "Hazardous Bin",
    General_Waste: "General Waste Bin",
    "Unknown / Still learning": "Unknown Bin",
    Unknown: "Unknown Bin"
};

const SUGGESTION_COLORS = {
    Compost: "#388e3c",
    Recycle: "#1976d2",
    Hazardous: "#d32f2f",
    General_Waste: "#504d4d",
    "Unknown / Still learning": "#9e9e9e",
    Unknown: "#9e9e9e"
};

const BIN_EMOJIS = {
    Compost: "🌱",
    Recycle: "♻️",
    Hazardous: "⚠️",
    General_Waste: "🗑️",
    "Unknown / Still learning": "❓",
    Unknown: "❓"
};

const CONFIDENCE_MESSAGES = {
    high: "🔥 Super confident!",
    medium: "👍 Pretty sure!",
    low: "🤔 Let me think..."
};

function getBinImage(category) {
    if (!category) return unknownImg;
    if (typeof category === "string") return BIN_IMAGES[category] || unknownImg;
    return BIN_IMAGES[category.name] || unknownImg;
}

function getBinLabel(category) {
    if (!category) return "Unknown Bin";
    if (typeof category === "string") return BIN_LABELS[category] || "Unknown Bin";
    return BIN_LABELS[category.name] || "Unknown Bin";
}

function getBinEmoji(category) {
    if (!category) return "❓";
    if (typeof category === "string") return BIN_EMOJIS[category] || "❓";
    return BIN_EMOJIS[category.name] || "❓";
}

function getConfidenceLevel(confidence) {
    if (confidence >= 80) return "high";
    if (confidence >= 60) return "medium";
    return "low";
}

export default function ClassificationPage() {
    const { isAuthenticated, user, setUser } = useAuth(); // Add setUser
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedbackText, setFeedbackText] = useState("");
    const [feedbackMsg, setFeedbackMsg] = useState("");
    const [classificationCount, setClassificationCount] = useState(0);
    const [streak, setStreak] = useState(0);
    const [streakDays, setStreakDays] = useState(0);
    const [lastClassificationDate, setLastClassificationDate] = useState(null);
    const [showCelebration, setShowCelebration] = useState(false);
    const [processingTime, setProcessingTime] = useState(0);
    const [animationKey, setAnimationKey] = useState(0);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [shouldRedirectBack, setShouldRedirectBack] = useState(false);
    const [userStats, setUserStats] = useState({ totalClassifications: 0, currentStreak: 0 });
    const [zoomOpen, setZoomOpen] = useState(false);
    const [zoomImage, setZoomImage] = useState(null);
    const inputRef = useRef();

    // Get bin info
    const category = result?.category?.name || result?.category || "Unknown";
    const binColor = SUGGESTION_COLORS[category] || "#8bc34a";
    const confidence = Number(result?.resultConfidence || result?.confidence || 0);
    const confidenceLevel = getConfidenceLevel(confidence);

    // Particles configuration
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: {
            color: {
                value: "transparent",
            },
        },
        fpsLimit: 120,
        interactivity: {
            events: {
                onClick: {
                    enable: true,
                    mode: "push",
                },
                onHover: {
                    enable: true,
                    mode: "repulse",
                },
                resize: true,
            },
            modes: {
                push: {
                    quantity: 2,
                },
                repulse: {
                    distance: 100,
                    duration: 0.4,
                },
            },
        },
        particles: {
            color: {
                value: ["#43a047", "#66bb6a", "#4caf50", "#8bc34a", "#c8e6c9"],
            },
            links: {
                enable: false,
            },
            move: {
                direction: "none",
                enable: true,
                outModes: {
                    default: "bounce",
                },
                random: true,
                speed: 1,
                straight: false,
            },
            number: {
                density: {
                    enable: true,
                    area: 2000,
                },
                value: 15,
            },
            opacity: {
                value: 0.3,
                random: true,
                animation: {
                    enable: true,
                    speed: 1,
                    minimumValue: 0.1,
                    sync: false,
                },
            },
            shape: {
                type: ["circle", "triangle", "polygon"],
                options: {
                    polygon: {
                        sides: 6,
                    },
                },
            },
            size: {
                value: { min: 8, max: 25 },
                random: true,
                animation: {
                    enable: true,
                    speed: 2,
                    minimumValue: 5,
                    sync: false,
                },
            },
        },
        detectRetina: true,
    };

    // Animation effects
    useEffect(() => {
        if (result) {
            setShowCelebration(true);
            setClassificationCount(prev => prev + 1);

            // Handle streak calculation based on days
            const today = new Date().toDateString();
            if (lastClassificationDate !== today) {
                setStreakDays(prev => prev + 1);
                setLastClassificationDate(today);
            }

            if (confidence >= 70) {
                setStreak(prev => prev + 1);
            } else {
                setStreak(0);
            }
            setTimeout(() => setShowCelebration(false), 3000);
        }
    }, [result, confidence, lastClassificationDate]);

    const handleFileSelect = (file) => {
        if (!file) return;
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setResult(null);
        setError("");
        setAnimationKey(prev => prev + 1);
    };

    const handleClassify = async () => {
        const startTime = Date.now();
        setLoading(true);
        setError("");
        setResult(null);
        setFeedbackMsg("");

        try {
            // Client-side file size validation BEFORE sending request
            const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB in bytes
            if (selectedFile && selectedFile.size > MAX_FILE_SIZE_BYTES) {
                const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
                const maxSizeMB = (MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(1);
                setError(`❌ File too large! Your file: ${fileSizeMB} MB, Maximum allowed: ${maxSizeMB} MB. Please compress your image or choose a smaller file.`);
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append("file", selectedFile);
            const res = await classifyImage(formData);
            setResult(res.data);
            setProcessingTime(Date.now() - startTime);

            // Refresh user data to update points after classification
            if (isAuthenticated) {
                try {
                    const profileResponse = await getProfile();
                    setUser(profileResponse.data);
                } catch (profileError) {
                    console.error('Failed to refresh user profile:', profileError);
                }
            }
        } catch (err) {
            console.error('Classification error:', err);

            // Check if it's specifically a file size error (backup check)
            const isFileSizeError = (
                err?.response?.status === 413 ||
                err?.response?.data?.error === 'FILE_SIZE_EXCEEDED' ||
                (err?.response?.data?.message && err.response.data.message.includes('file size')) ||
                (err?.response?.data?.message && err.response.data.message.includes('Maximum upload size')) ||
                (err?.response?.data && typeof err.response.data === 'string' &&
                    (err.response.data.includes('MaxUploadSizeExceededException') ||
                        err.response.data.includes('file size')))
            );

            if (isFileSizeError) {
                // File size exceeded error - show specific message with actual file size
                if (selectedFile && selectedFile.size) {
                    const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
                    setError(`❌ File too large! Your file: ${fileSizeMB} MB, Maximum allowed: 5.0 MB. Please compress your image or choose a smaller file.`);
                } else {
                    setError(`❌ File too large! Maximum allowed: 5.0 MB. Please compress your image or choose a smaller file.`);
                }
            } else if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
                // Network or CORS errors
                setError("❌ Network error. Please check if the server is running and try again.");
            } else if (err?.response?.status === 403) {
                // 403 Forbidden but not file size related
                setError("❌ Access denied. Please check your authentication and try again.");
            } else {
                // Other errors - use generic message
                setError("❌ Failed to classify image. Please try again!");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleNewImage = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setResult(null);
        setError("");
        setAnimationKey(prev => prev + 1);
        inputRef.current?.click();
    };

    const handleFeedbackClick = () => {
        if (!isAuthenticated) {
            setShouldRedirectBack(true);
            setShowLoginPrompt(true);
        } else {
            setFeedbackOpen(true);
        }
    };

    const handleZoomOpen = (image) => {
        setZoomImage(image);
        setZoomOpen(true);
    };

    const handleZoomClose = () => {
        setZoomOpen(false);
        setZoomImage(null);
    };

    // Store current page state for redirect after login
    useEffect(() => {
        if (shouldRedirectBack && result) {
            localStorage.setItem('classificationResult', JSON.stringify(result));
            localStorage.setItem('redirectToClassification', 'true');
        }
    }, [shouldRedirectBack, result]);

    // Fetch user stats on component mount and when user changes
    useEffect(() => {
        const fetchUserStats = async () => {
            if (isAuthenticated && user) {
                try {
                    // Fetch user history to get real classification count
                    const historyResponse = await getUserHistory();
                    const history = historyResponse.data || [];

                    // Calculate real classification count
                    const totalCount = history.length;
                    setClassificationCount(totalCount);

                    // Calculate streak based on consecutive days with classifications
                    const streak = calculateStreak(history);
                    setStreakDays(streak);

                    // Update userStats for the dashboard
                    setUserStats({
                        totalClassifications: totalCount,
                        currentStreak: streak
                    });
                } catch (error) {
                    console.error('Failed to fetch user stats:', error);
                    // Keep default values if fetch fails
                }
            } else {
                // Reset stats for non-authenticated users
                setClassificationCount(0);
                setStreakDays(0);
                setUserStats({ totalClassifications: 0, currentStreak: 0 });
            }
        };

        fetchUserStats();
    }, [isAuthenticated, user]);

    // Helper function to calculate streak
    const calculateStreak = (history) => {
        if (!history || history.length === 0) return 0;

        // Group classifications by date
        const dateGroups = {};
        history.forEach(item => {
            const date = new Date(item.classifiedAt).toDateString();
            if (!dateGroups[date]) {
                dateGroups[date] = 0;
            }
            dateGroups[date]++;
        });

        // Get sorted unique dates
        const uniqueDates = Object.keys(dateGroups).sort((a, b) => new Date(b) - new Date(a));

        if (uniqueDates.length === 0) return 0;

        // Calculate consecutive days from today backwards
        let streak = 0;
        const today = new Date().toDateString();

        for (let i = 0; i < uniqueDates.length; i++) {
            const currentDate = new Date(uniqueDates[i]);
            const expectedDate = new Date();
            expectedDate.setDate(expectedDate.getDate() - i);

            if (currentDate.toDateString() === expectedDate.toDateString()) {
                streak++;
            } else {
                break;
            }
        }

        return streak;
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: `linear-gradient(135deg, 
          rgba(76, 175, 80, 0.1) 0%, 
          rgba(67, 160, 71, 0.05) 25%,
          rgba(139, 195, 74, 0.1) 50%,
          rgba(156, 204, 101, 0.05) 75%,
          rgba(165, 214, 167, 0.1) 100%)`,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Particles Background */}
            <Particles
                id="tsparticles"
                init={particlesInit}
                options={particlesConfig}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 0,
                }}
            />

            {/* Floating Eco Elements */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                    pointerEvents: "none",
                    overflow: "hidden",
                }}
            >
                {/* Floating Leaves */}
                {[...Array(8)].map((_, i) => (
                    <Box
                        key={`leaf-${i}`}
                        sx={{
                            position: "absolute",
                            fontSize: { xs: "2rem", md: "3rem" },
                            opacity: 0.15,
                            animation: `float-${i % 3} ${8 + i * 2}s infinite linear`,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            transform: `rotate(${Math.random() * 360}deg)`,
                        }}
                    >
                        {["🌿", "🍃", "🌱", "♻️", "🌍"][i % 5]}
                    </Box>
                ))}

                {/* Floating Geometric Shapes */}
                {[...Array(6)].map((_, i) => (
                    <Box
                        key={`shape-${i}`}
                        sx={{
                            position: "absolute",
                            width: { xs: 60, md: 80 },
                            height: { xs: 60, md: 80 },
                            borderRadius: i % 2 === 0 ? "50%" : "20%",
                            background: `linear-gradient(45deg, 
                rgba(67, 160, 71, 0.1) 0%, 
                rgba(139, 195, 74, 0.15) 100%)`,
                            border: `2px solid rgba(67, 160, 71, 0.2)`,
                            animation: `geometric-float ${10 + i * 3}s infinite ease-in-out`,
                            left: `${10 + (i * 15)}%`,
                            top: `${20 + (i * 10)}%`,
                        }}
                    />
                ))}
            </Box>

            {/* Header Stats Bar */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    bgcolor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderBottom: "1px solid rgba(67, 160, 71, 0.1)",
                    py: 1
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Stack direction="row" spacing={3} alignItems="center">
                                <Chip
                                    icon={<RecyclingIcon />}
                                    label={`${classificationCount} Classifications`}
                                    color="success"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                                {streakDays > 0 && (
                                    <Chip
                                        icon={<LocalFireDepartmentIcon />}
                                        label={`${streakDays} Day${streakDays > 1 ? 's' : ''} Streak`}
                                        sx={{
                                            bgcolor: "#ff6f00",
                                            color: "white",
                                            fontWeight: 600,
                                            "& .MuiChip-icon": { color: "white" }
                                        }}
                                    />
                                )}
                                {processingTime > 0 && (
                                    <Chip
                                        icon={<SpeedIcon />}
                                        label={`${processingTime}ms`}
                                        color="info"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {isAuthenticated && (
                                    <>
                                        <Tooltip title="View History">
                                            <IconButton
                                                color="primary"
                                                component={RouterLink}
                                                to="/history"
                                                sx={{ bgcolor: "rgba(67, 160, 71, 0.1)" }}
                                            >
                                                <HistoryIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Leaderboard">
                                            <IconButton
                                                color="warning"
                                                component={RouterLink}
                                                to="/leaderboard"
                                                sx={{ bgcolor: "rgba(255, 193, 7, 0.1)" }}
                                            >
                                                <LeaderboardIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ py: 4, position: "relative", zIndex: 2 }}>
                {/* Title Section */}
                <Box textAlign="center" mb={4}>
                    <Typography
                        variant="h2"
                        fontWeight={900}
                        sx={{
                            background: "linear-gradient(45deg, #43a047 30%, #66bb6a 90%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            mb: 1,
                            fontSize: { xs: "2.5rem", md: "3.5rem" }
                        }}
                    >
                        🌱 Waste Classifier
                    </Typography>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 3, fontWeight: 500 }}
                    >
                        Upload an image and let AI help you sort waste responsibly!
                    </Typography>
                </Box>

                {/* Main Classification Interface */}
                <Grid container spacing={4} justifyContent="center">
                    {/* Left Side - Upload & Result */}
                    <Grid item xs={12} lg={7}>
                        <Fade in={true} timeout={1000}>
                            <Card
                                key={animationKey}
                                elevation={0}
                                sx={{
                                    background: result
                                        ? `linear-gradient(135deg, ${binColor}10 0%, ${binColor}05 100%)`
                                        : "linear-gradient(135deg, #ffffff 0%, #f8faf8 100%)",
                                    borderRadius: 6,
                                    border: result
                                        ? `3px solid ${binColor}`
                                        : "3px solid transparent",
                                    p: 4,
                                    textAlign: "center",
                                    position: "relative",
                                    overflow: "hidden",
                                    minHeight: 500,
                                    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&::before": result ? {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: `radial-gradient(circle at 50% 50%, ${binColor}15 0%, transparent 70%)`,
                                        zIndex: 0
                                    } : {}
                                }}
                            >
                                <Box position="relative" zIndex={1}>
                                    {!selectedFile ? (
                                        /* Initial Upload State */
                                        <Box>
                                            <Zoom in={true} timeout={800}>
                                                <Box
                                                    sx={{
                                                        width: 200,
                                                        height: 200,
                                                        borderRadius: "50%",
                                                        border: "4px dashed #43a047",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        mx: "auto",
                                                        mb: 3,
                                                        bgcolor: "rgba(67, 160, 71, 0.05)",
                                                        cursor: "pointer",
                                                        transition: "all 0.3s ease",
                                                        "&:hover": {
                                                            transform: "scale(1.05)",
                                                            bgcolor: "rgba(67, 160, 71, 0.1)",
                                                            borderColor: "#388e3c"
                                                        }
                                                    }}
                                                    component="label"
                                                >
                                                    <PhotoCameraIcon sx={{ fontSize: 80, color: "#43a047" }} />
                                                    <input
                                                        ref={inputRef}
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={e => handleFileSelect(e.target.files[0])}
                                                    />
                                                </Box>
                                            </Zoom>

                                            <Typography variant="h4" fontWeight={700} color="primary" mb={2}>
                                                📸 Upload Your Waste Image
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary" mb={3}>
                                                Take a clear photo of your waste item for accurate classification
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={<CloudUploadIcon />}
                                                component="label"
                                                sx={{
                                                    py: 1.5,
                                                    px: 4,
                                                    fontSize: "1.1rem",
                                                    fontWeight: 700,
                                                    borderRadius: 8,
                                                    background: "linear-gradient(45deg, #43a047 30%, #66bb6a 90%)",
                                                    boxShadow: "0 8px 25px rgba(67, 160, 71, 0.3)",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "0 12px 35px rgba(67, 160, 71, 0.4)"
                                                    }
                                                }}
                                            >
                                                Choose Image
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={e => handleFileSelect(e.target.files[0])}
                                                />
                                            </Button>

                                            {/* Pro Tips - Compact Version */}
                                            <Box
                                                sx={{
                                                    mt: 4,
                                                    p: 3,
                                                    borderRadius: 4,
                                                    background: "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(100, 181, 246, 0.05) 100%)",
                                                    border: "2px solid rgba(33, 150, 243, 0.2)"
                                                }}
                                            >
                                                <Typography variant="h6" fontWeight={700} color="primary" mb={2} textAlign="center">
                                                    💡 Quick Tips for Best Results
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                                            <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50", mr: 1 }} />
                                                            Clear, well-lit photos
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                                            <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50", mr: 1 }} />
                                                            Item clearly in frame
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ display: "flex", alignItems: "center" }}>
                                                            <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50", mr: 1 }} />
                                                            Avoid blurry images
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ display: "flex", alignItems: "center" }}>
                                                            <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50", mr: 1 }} />
                                                            Try different angles
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Box>
                                    ) : !result ? (
                                        /* Preview State */
                                        <Box>
                                            <Zoom in={true} timeout={500}>
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        display: "inline-block",
                                                        mb: 3
                                                    }}
                                                >
                                                    <img
                                                        src={previewUrl}
                                                        alt="Preview"
                                                        style={{
                                                            width: "100%",
                                                            maxWidth: 300,
                                                            height: 300,
                                                            objectFit: "cover",
                                                            borderRadius: 20,
                                                            border: "4px solid #43a047",
                                                            boxShadow: "0 15px 35px rgba(67, 160, 71, 0.3)"
                                                        }}
                                                        onClick={() => handleZoomOpen(previewUrl)}
                                                    />
                                                    <IconButton
                                                        onClick={handleNewImage}
                                                        sx={{
                                                            position: "absolute",
                                                            top: -10,
                                                            right: -10,
                                                            bgcolor: "#ff5722",
                                                            color: "white",
                                                            "&:hover": { bgcolor: "#e64a19" }
                                                        }}
                                                    >
                                                        <RestartAltIcon />
                                                    </IconButton>
                                                </Box>
                                            </Zoom>

                                            <Typography variant="h5" fontWeight={700} mb={2}>
                                                🔍 Ready to Classify!
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <PlayArrowIcon />}
                                                onClick={handleClassify}
                                                disabled={loading}
                                                sx={{
                                                    py: 2,
                                                    px: 6,
                                                    fontSize: "1.2rem",
                                                    fontWeight: 700,
                                                    borderRadius: 8,
                                                    background: loading
                                                        ? "linear-gradient(45deg, #9e9e9e 30%, #bdbdbd 90%)"
                                                        : "linear-gradient(45deg, #ff6f00 30%, #ffa726 90%)",
                                                    boxShadow: loading
                                                        ? "0 8px 25px rgba(158, 158, 158, 0.3)"
                                                        : "0 8px 25px rgba(255, 111, 0, 0.3)",
                                                    "&:hover": {
                                                        transform: loading ? "none" : "translateY(-2px)",
                                                        boxShadow: loading
                                                            ? "0 8px 25px rgba(158, 158, 158, 0.3)"
                                                            : "0 12px 35px rgba(255, 111, 0, 0.4)"
                                                    }
                                                }}
                                            >
                                                {loading ? "Analyzing..." : "Classify Now"}
                                            </Button>
                                        </Box>
                                    ) : (
                                        /* Result State */
                                        <Box>
                                            {/* Celebration Animation */}
                                            {showCelebration && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: 20,
                                                        right: 20,
                                                        zIndex: 10
                                                    }}
                                                >
                                                    <Zoom in={showCelebration} timeout={500}>
                                                        <CelebrationIcon
                                                            sx={{
                                                                fontSize: 60,
                                                                color: "#ffd700",
                                                                animation: "bounce 1s infinite"
                                                            }}
                                                        />
                                                    </Zoom>
                                                </Box>
                                            )}

                                            <Slide direction="up" in={true} timeout={800}>
                                                <Box>
                                                    {/* Bin Image with Pulse Effect - Full Display */}
                                                    <Box
                                                        sx={{
                                                            position: "relative",
                                                            display: "inline-block",
                                                            mb: 2
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 160,
                                                                height: 160,
                                                                borderRadius: 3,
                                                                border: `4px solid ${binColor}`,
                                                                boxShadow: `0 0 30px ${binColor}50`,
                                                                animation: confidence >= 80 ? "pulse 2s infinite" : "none",
                                                                overflow: "hidden",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "rgba(255, 255, 255, 0.95)",
                                                                position: "relative",
                                                                cursor: "pointer",
                                                                transition: "all 0.3s ease",
                                                                "&:hover": {
                                                                    transform: "scale(1.05)",
                                                                    boxShadow: `0 0 40px ${binColor}80`,
                                                                    "& .zoom-indicator": {
                                                                        opacity: 1
                                                                    }
                                                                },
                                                                "@keyframes pulse": {
                                                                    "0%": { transform: "scale(1)" },
                                                                    "50%": { transform: "scale(1.05)" },
                                                                    "100%": { transform: "scale(1)" }
                                                                }
                                                            }}
                                                            onClick={() => handleZoomOpen(getBinImage(category))}
                                                        >
                                                            <img
                                                                src={getBinImage(category)}
                                                                alt={getBinLabel(category)}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "contain",
                                                                    padding: "12px"
                                                                }}
                                                            />
                                                            {/* Zoom Indicator Icon */}
                                                            <Box
                                                                className="zoom-indicator"
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: 8,
                                                                    right: 8,
                                                                    bgcolor: "rgba(0, 0, 0, 0.7)",
                                                                    borderRadius: "50%",
                                                                    width: 30,
                                                                    height: 30,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    opacity: 0,
                                                                    transition: "opacity 0.3s ease",
                                                                    zIndex: 3
                                                                }}
                                                            >
                                                                <ZoomInIcon sx={{ fontSize: 16, color: "white" }} />
                                                            </Box>
                                                            {/* Subtle gradient overlay for depth */}
                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    background: `linear-gradient(135deg, transparent 0%, ${binColor}08 100%)`,
                                                                    zIndex: 1,
                                                                    pointerEvents: "none"
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                bottom: -8,
                                                                right: -8,
                                                                bgcolor: binColor,
                                                                borderRadius: "50%",
                                                                width: 44,
                                                                height: 44,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "1.6rem",
                                                                boxShadow: `0 4px 12px ${binColor}40`,
                                                                border: "3px solid white"
                                                            }}
                                                        >
                                                            {getBinEmoji(category)}
                                                        </Box>
                                                    </Box>

                                                    {/* Result Info */}
                                                    <Typography
                                                        variant="h3"
                                                        fontWeight={900}
                                                        sx={{
                                                            color: binColor,
                                                            mb: 1,
                                                            textShadow: `0 2px 4px ${binColor}30`,
                                                            fontSize: { xs: "2rem", md: "2.5rem" }
                                                        }}
                                                    >
                                                        {getBinLabel(category)}
                                                    </Typography>

                                                    <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
                                                        {typeof result.category === "string"
                                                            ? result.category
                                                            : result.category?.name}
                                                    </Typography>

                                                    {/* Confidence Chip with Animation */}
                                                    <Chip
                                                        icon={confidenceLevel === "high" ? <FlashOnIcon /> : <StarIcon />}
                                                        label={`${confidence.toFixed(1)}% ${CONFIDENCE_MESSAGES[confidenceLevel]}`}
                                                        sx={{
                                                            mb: 3,
                                                            fontSize: "1rem",
                                                            py: 2,
                                                            px: 3,
                                                            fontWeight: 700,
                                                            bgcolor: confidence >= 80 ? "#4caf50" : confidence >= 60 ? "#ff9800" : "#607d8b",
                                                            color: "white",
                                                            "& .MuiChip-icon": { color: "white", fontSize: "1.2rem" },
                                                            animation: confidence >= 80 ? "glow 2s infinite" : "none",
                                                            "@keyframes glow": {
                                                                "0%, 100%": { boxShadow: `0 0 20px ${binColor}50` },
                                                                "50%": { boxShadow: `0 0 30px ${binColor}80` }
                                                            }
                                                        }}
                                                    />

                                                    {/* Action Suggestion */}
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            background: `linear-gradient(135deg, ${binColor}20 0%, ${binColor}10 100%)`,
                                                            borderRadius: 6,
                                                            p: 4,
                                                            mb: 3,
                                                            border: `3px solid ${binColor}40`,
                                                            position: "relative",
                                                            overflow: "hidden",
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: `radial-gradient(circle at 30% 30%, ${binColor}15 0%, transparent 70%)`,
                                                                zIndex: 0
                                                            }
                                                        }}
                                                    >
                                                        <Box position="relative" zIndex={1}>
                                                            <Typography
                                                                variant="h6"
                                                                fontWeight={700}
                                                                sx={{
                                                                    mb: 2,
                                                                    fontSize: "1.1rem",
                                                                    color: binColor,
                                                                    opacity: 0.8
                                                                }}
                                                            >
                                                                💡 What to do:
                                                            </Typography>
                                                            <Typography
                                                                variant="h4"
                                                                fontWeight={900}
                                                                sx={{
                                                                    background: `linear-gradient(45deg, ${binColor} 0%, ${binColor}CC 100%)`,
                                                                    backgroundClip: "text",
                                                                    WebkitBackgroundClip: "text",
                                                                    WebkitTextFillColor: "transparent",
                                                                    textShadow: `0 2px 8px ${binColor}30`,
                                                                    fontSize: { xs: "1.8rem", md: "2.2rem" },
                                                                    lineHeight: 1.2,
                                                                    letterSpacing: "0.5px"
                                                                }}
                                                            >
                                                                {result.suggestion}
                                                            </Typography>
                                                        </Box>
                                                    </Paper>

                                                    {/* AI Explanation */}
                                                    {result.explanation && (
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                background: "linear-gradient(135deg, rgba(103, 58, 183, 0.15) 0%, rgba(63, 81, 181, 0.1) 100%)",
                                                                borderRadius: 6,
                                                                p: 4,
                                                                mb: 3,
                                                                border: "3px solid rgba(103, 58, 183, 0.3)",
                                                                position: "relative",
                                                                overflow: "hidden"
                                                            }}
                                                        >
                                                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                                                <Box
                                                                    sx={{
                                                                        width: 40,
                                                                        height: 40,
                                                                        borderRadius: "50%",
                                                                        background: "linear-gradient(135deg, #673ab7 0%, #3f51b5 100%)",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        mr: 2,
                                                                        boxShadow: "0 4px 15px rgba(103, 58, 183, 0.4)"
                                                                    }}
                                                                >
                                                                    <Typography fontSize="1.5rem">🧠</Typography>
                                                                </Box>
                                                                <Typography
                                                                    variant="h6"
                                                                    fontWeight={700}
                                                                    sx={{
                                                                        background: "linear-gradient(45deg, #673ab7 30%, #3f51b5 90%)",
                                                                        backgroundClip: "text",
                                                                        WebkitBackgroundClip: "text",
                                                                        WebkitTextFillColor: "transparent"
                                                                    }}
                                                                >
                                                                    AI Analysis Explanation
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                variant="body1"
                                                                fontWeight={500}
                                                                sx={{
                                                                    color: "#424242",
                                                                    lineHeight: 1.6,
                                                                    fontSize: "1.1rem"
                                                                }}
                                                            >
                                                                {result.explanation}
                                                            </Typography>
                                                        </Paper>
                                                    )}

                                                    {/* Action Buttons */}
                                                    <Stack direction="row" spacing={2} justifyContent="center">
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<ShuffleIcon />}
                                                            onClick={handleNewImage}
                                                            sx={{
                                                                bgcolor: "#43a047",
                                                                fontWeight: 700,
                                                                "&:hover": { bgcolor: "#388e3c" }
                                                            }}
                                                        >
                                                            Try Another
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={handleFeedbackClick}
                                                            sx={{
                                                                borderColor: binColor,
                                                                color: binColor,
                                                                fontWeight: 700,
                                                                "&:hover": {
                                                                    bgcolor: `${binColor}10`,
                                                                    borderColor: binColor
                                                                }
                                                            }}
                                                        >
                                                            Give Feedback
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            </Slide>
                                        </Box>
                                    )}
                                </Box>

                                {/* Progress Bar for Loading */}
                                {loading && (
                                    <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
                                        <LinearProgress
                                            sx={{
                                                height: 6,
                                                borderRadius: "0 0 20px 20px",
                                                "& .MuiLinearProgress-bar": {
                                                    background: "linear-gradient(45deg, #43a047 30%, #66bb6a 90%)"
                                                }
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Error Alert */}
                                {error && (
                                    <Fade in={!!error} timeout={500}>
                                        <Alert
                                            severity="error"
                                            sx={{
                                                mt: 3,
                                                borderRadius: 3,
                                                fontWeight: 600
                                            }}
                                            action={
                                                <Button color="inherit" size="small" onClick={() => setError("")}>
                                                    Dismiss
                                                </Button>
                                            }
                                        >
                                            {error}
                                        </Alert>
                                    </Fade>
                                )}
                            </Card>
                        </Fade>
                    </Grid>

                    {/* Right Side - Details & Gamification */}
                    <Grid item xs={12} lg={5}>
                        <Stack spacing={3}>
                            {/* Educational Tips Section - Show relevant tips */}
                            <Fade in={true} timeout={1000}>
                                <Box>
                                    <TipDisplay
                                        categoryId={result?.category?.id}
                                        compact={true}
                                        showRefresh={true}
                                    />
                                </Box>
                            </Fade>

                            {/* System Analytics Card - Only show when there's a result */}
                            {result && (
                                <Fade in={true} timeout={1200}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            background: "linear-gradient(135deg, #ffffff 0%, #f8faf8 100%)",
                                            borderRadius: 6,
                                            border: "3px solid #e8f5e9",
                                            overflow: "hidden",
                                            position: "relative",
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: 6,
                                                background: "linear-gradient(90deg, #43a047 0%, #66bb6a 50%, #8bc34a 100%)",
                                                zIndex: 1
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 4, pt: 5 }}>
                                            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                                                <Box
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        borderRadius: "50%",
                                                        background: "linear-gradient(135deg, #43a047 0%, #66bb6a 100%)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        boxShadow: "0 4px 20px rgba(67, 160, 71, 0.3)"
                                                    }}
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: 28, color: "white" }} />
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="h5"
                                                        fontWeight={800}
                                                        sx={{
                                                            background: "linear-gradient(45deg, #43a047 30%, #2e7d32 90%)",
                                                            backgroundClip: "text",
                                                            WebkitBackgroundClip: "text",
                                                            WebkitTextFillColor: "transparent",
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        System Analytics
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                        Classification Performance Metrics
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            <Grid container spacing={3}>
                                                <Grid item xs={6}>
                                                    <Box
                                                        sx={{
                                                            p: 3,
                                                            borderRadius: 4,
                                                            background: "linear-gradient(135deg, rgba(67, 160, 71, 0.1) 0%, rgba(139, 195, 74, 0.05) 100%)",
                                                            border: "2px solid rgba(67, 160, 71, 0.2)",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        <Typography variant="h4" fontWeight={800} color="success.main" mb={1}>
                                                            {result.aiModelVersion?.versionName || "v1.0"}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                            AI Model
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box
                                                        sx={{
                                                            p: 3,
                                                            borderRadius: 4,
                                                            background: "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(100, 181, 246, 0.05) 100%)",
                                                            border: "2px solid rgba(33, 150, 243, 0.2)",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        <Typography variant="h4" fontWeight={800} color="info.main" mb={1}>
                                                            {processingTime}ms
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                            Speed
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box
                                                        sx={{
                                                            p: 3,
                                                            borderRadius: 4,
                                                            background: "linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(186, 104, 200, 0.05) 100%)",
                                                            border: "2px solid rgba(156, 39, 176, 0.2)",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        <Typography variant="h4" fontWeight={800} color="secondary.main" mb={1}>
                                                            {result.apiVersion || "v1"}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                            API Version
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box
                                                        sx={{
                                                            p: 3,
                                                            borderRadius: 4,
                                                            background: "linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)",
                                                            border: "2px solid rgba(255, 152, 0, 0.2)",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        <Typography variant="h6" fontWeight={800} sx={{ color: "#ff9800", mb: 1 }}>
                                                            {result.classifiedAt
                                                                ? new Date(result.classifiedAt).toLocaleTimeString()
                                                                : new Date().toLocaleTimeString()}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                            Timestamp
                                                        </Typography>
                                                    </Box>
                                                </Grid>

                                                {result.category?.description && (
                                                    <Grid item xs={12}>
                                                        <Box
                                                            sx={{
                                                                p: 3,
                                                                borderRadius: 4,
                                                                background: "linear-gradient(135deg, rgba(103, 58, 183, 0.1) 0%, rgba(159, 168, 218, 0.05) 100%)",
                                                                border: "2px solid rgba(103, 58, 183, 0.2)"
                                                            }}
                                                        >
                                                            <Typography variant="h6" fontWeight={700} color="primary.main" mb={2}>
                                                                📖 Category Information
                                                            </Typography>
                                                            <Typography variant="body1" fontWeight={500} lineHeight={1.6}>
                                                                {result.category.description}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            )}

                            {/* Authentication/User Engagement Section */}
                            <Fade in={true} timeout={1400}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: isAuthenticated
                                            ? "linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)"
                                            : "linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)",
                                        borderRadius: 6,
                                        border: isAuthenticated ? "3px solid #4caf50" : "3px solid #ff9800",
                                        overflow: "hidden",
                                        position: "relative",
                                        "&::before": !isAuthenticated ? {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 8,
                                            background: "linear-gradient(90deg, #ff9800 0%, #ffa726 50%, #ffb74d 100%)",
                                            zIndex: 1
                                        } : {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 8,
                                            background: "linear-gradient(90deg, #4caf50 0%, #66bb6a 50%, #81c784 100%)",
                                            zIndex: 1
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 4, pt: 5 }}>
                                        {isAuthenticated ? (
                                            /* Authenticated User Dashboard */
                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                                                    <Box
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            borderRadius: "50%",
                                                            background: "linear-gradient(135deg, #ffd700 0%, #ffb300 100%)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            boxShadow: "0 6px 25px rgba(255, 193, 7, 0.4)",
                                                            border: "3px solid white"
                                                        }}
                                                    >
                                                        <EmojiEventsOutlinedIcon sx={{ fontSize: 32, color: "white" }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            variant="h4"
                                                            fontWeight={900}
                                                            sx={{
                                                                background: "linear-gradient(45deg, #4caf50 30%, #2e7d32 90%)",
                                                                backgroundClip: "text",
                                                                WebkitBackgroundClip: "text",
                                                                WebkitTextFillColor: "transparent",
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            🏆 Your Dashboard
                                                        </Typography>
                                                        <Typography variant="body1" color="text.secondary" fontWeight={600}>
                                                            Keep up the amazing eco work!
                                                        </Typography>
                                                    </Box>
                                                </Stack>

                                                {/* Enhanced User Stats Grid */}
                                                <Grid container spacing={3} mb={4}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box
                                                            sx={{
                                                                textAlign: "center",
                                                                p: 4,
                                                                borderRadius: 6,
                                                                background: "linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(139, 195, 74, 0.1) 100%)",
                                                                border: "3px solid rgba(76, 175, 80, 0.3)",
                                                                position: "relative",
                                                                overflow: "hidden",
                                                                "&::before": {
                                                                    content: '""',
                                                                    position: "absolute",
                                                                    top: -20,
                                                                    right: -20,
                                                                    width: 80,
                                                                    height: 80,
                                                                    borderRadius: "50%",
                                                                    background: "rgba(76, 175, 80, 0.1)",
                                                                    zIndex: 0
                                                                }
                                                            }}
                                                        >
                                                            <Box position="relative" zIndex={1}>
                                                                <Typography variant="h2" fontWeight={900} color="success.main" mb={1} sx={{ fontSize: "3rem" }}>
                                                                    {classificationCount}
                                                                </Typography>
                                                                <Typography variant="h6" fontWeight={800} color="success.dark" mb={1}>
                                                                    Total Classifications
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                                    Keep sorting to save our planet! 🌍
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box
                                                            sx={{
                                                                textAlign: "center",
                                                                p: 4,
                                                                borderRadius: 6,
                                                                background: "linear-gradient(135deg, rgba(255, 111, 0, 0.2) 0%, rgba(255, 167, 38, 0.1) 100%)",
                                                                border: "3px solid rgba(255, 111, 0, 0.3)",
                                                                position: "relative",
                                                                overflow: "hidden",
                                                                "&::before": {
                                                                    content: '""',
                                                                    position: "absolute",
                                                                    top: -20,
                                                                    left: -20,
                                                                    width: 80,
                                                                    height: 80,
                                                                    borderRadius: "50%",
                                                                    background: "rgba(255, 111, 0, 0.1)",
                                                                    zIndex: 0
                                                                }
                                                            }}
                                                        >
                                                            <Box position="relative" zIndex={1}>
                                                                <Typography variant="h2" fontWeight={900} sx={{ color: "#ff6f00", mb: 1, fontSize: "3rem" }}>
                                                                    {streakDays}
                                                                </Typography>
                                                                <Typography variant="h6" fontWeight={800} sx={{ color: "#e65100", mb: 1 }}>
                                                                    Day Streak 🔥
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                                    Consistency is the key to success!
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                </Grid>

                                                {/* Quick Actions with Enhanced Design */}
                                                <Stack spacing={3}>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<LeaderboardIcon />}
                                                        component={RouterLink}
                                                        to="/leaderboard"
                                                        fullWidth
                                                        sx={{
                                                            fontWeight: 800,
                                                            py: 2.5,
                                                            fontSize: "1.2rem",
                                                            borderRadius: 6,
                                                            background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                                                            boxShadow: "0 8px 25px rgba(25, 118, 210, 0.4)",
                                                            position: "relative",
                                                            overflow: "hidden",
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "-100%",
                                                                width: "100%",
                                                                height: "100%",
                                                                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                                                                transition: "left 0.5s",
                                                            },
                                                            "&:hover": {
                                                                transform: "translateY(-3px)",
                                                                boxShadow: "0 12px 35px rgba(25, 118, 210, 0.5)",
                                                                "&::before": {
                                                                    left: "100%"
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        🏆 Compete on Global Leaderboard
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<HistoryIcon />}
                                                        component={RouterLink}
                                                        to="/history"
                                                        fullWidth
                                                        sx={{
                                                            fontWeight: 800,
                                                            py: 2.5,
                                                            fontSize: "1.2rem",
                                                            borderRadius: 6,
                                                            borderWidth: 3,
                                                            borderColor: "#4caf50",
                                                            color: "#2e7d32",
                                                            position: "relative",
                                                            overflow: "hidden",
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "-100%",
                                                                width: "100%",
                                                                height: "100%",
                                                                background: "linear-gradient(90deg, transparent, rgba(76,175,80,0.1), transparent)",
                                                                transition: "left 0.5s",
                                                            },
                                                            "&:hover": {
                                                                borderWidth: 3,
                                                                borderColor: "#388e3c",
                                                                bgcolor: "rgba(76, 175, 80, 0.1)",
                                                                transform: "translateY(-2px)",
                                                                "&::before": {
                                                                    left: "100%"
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        📊 View Your Classification History
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        ) : (
                                            /* Enhanced Guest User CTA */
                                            <Box textAlign="center">
                                                {/* Hero Icon with Animation */}
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        display: "inline-block",
                                                        mb: 4
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 120,
                                                            height: 120,
                                                            borderRadius: "50%",
                                                            background: "linear-gradient(135deg, #ff9800 0%, #ffa726 100%)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            mx: "auto",
                                                            boxShadow: "0 10px 40px rgba(255, 152, 0, 0.4)",
                                                            border: "4px solid white",
                                                            animation: "pulse-glow 3s infinite",
                                                            "@keyframes pulse-glow": {
                                                                "0%, 100%": {
                                                                    boxShadow: "0 10px 40px rgba(255, 152, 0, 0.4), 0 0 0 0 rgba(255, 152, 0, 0.7)"
                                                                },
                                                                "50%": {
                                                                    boxShadow: "0 15px 50px rgba(255, 152, 0, 0.6), 0 0 0 15px rgba(255, 152, 0, 0)"
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Typography fontSize="3.5rem">🚀</Typography>
                                                    </Box>
                                                    {/* Floating Elements */}
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            top: -10,
                                                            right: -10,
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            animation: "bounce-float 2s infinite",
                                                            "@keyframes bounce-float": {
                                                                "0%, 100%": { transform: "translateY(0px) scale(1)" },
                                                                "50%": { transform: "translateY(-10px) scale(1.1)" }
                                                            }
                                                        }}
                                                    >
                                                        <Typography fontSize="1.2rem">🌱</Typography>
                                                    </Box>
                                                </Box>

                                                <Typography
                                                    variant="h3"
                                                    fontWeight={900}
                                                    sx={{
                                                        background: "linear-gradient(45deg, #ff9800 30%, #ffa726 90%)",
                                                        backgroundClip: "text",
                                                        WebkitBackgroundClip: "text",
                                                        WebkitTextFillColor: "transparent",
                                                        mb: 2,
                                                        fontSize: { xs: "2.5rem", md: "3rem" }
                                                    }}
                                                >
                                                    🎯 Join the Eco Revolution!
                                                </Typography>

                                                <Typography variant="h6" fontWeight={600} color="text.secondary" mb={4} lineHeight={1.6} sx={{ maxWidth: 400, mx: "auto" }}>
                                                    Track your impact, compete with eco-warriors worldwide, and earn amazing rewards for saving our planet! 🌍✨
                                                </Typography>

                                                {/* Feature Highlights */}
                                                <Grid container spacing={2} mb={4}>
                                                    <Grid item xs={4}>
                                                        <Box textAlign="center">
                                                            <Box
                                                                sx={{
                                                                    width: 60,
                                                                    height: 60,
                                                                    borderRadius: "50%",
                                                                    background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    mx: "auto",
                                                                    mb: 1,
                                                                    boxShadow: "0 4px 15px rgba(76,175,80,0.3)"
                                                                }}
                                                            >
                                                                <Typography fontSize="1.8rem">📊</Typography>
                                                            </Box>
                                                            <Typography variant="body2" fontWeight={700} color="text.primary">
                                                                Track Progress
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Box textAlign="center">
                                                            <Box
                                                                sx={{
                                                                    width: 60,
                                                                    height: 60,
                                                                    borderRadius: "50%",
                                                                    background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    mx: "auto",
                                                                    mb: 1,
                                                                    boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)"
                                                                }}
                                                            >
                                                                <Typography fontSize="1.8rem">🏆</Typography>
                                                            </Box>
                                                            <Typography variant="body2" fontWeight={700} color="text.primary">
                                                                Global Ranking
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Box textAlign="center">
                                                            <Box
                                                                sx={{
                                                                    width: 60,
                                                                    height: 60,
                                                                    borderRadius: "50%",
                                                                    background: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    mx: "auto",
                                                                    mb: 1,
                                                                    boxShadow: "0 4px 15px rgba(156, 39, 176, 0.3)"
                                                                }}
                                                            >
                                                                <Typography fontSize="1.8rem">🎁</Typography>
                                                            </Box>
                                                            <Typography variant="body2" fontWeight={700} color="text.primary">
                                                                Eco Rewards
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>

                                                <Stack spacing={3}>
                                                    <Button
                                                        variant="contained"
                                                        component={RouterLink}
                                                        to="/register"
                                                        fullWidth
                                                        sx={{
                                                            py: 3,
                                                            fontSize: "1.3rem",
                                                            fontWeight: 900,
                                                            borderRadius: 6,
                                                            background: "linear-gradient(45deg, #43a047 30%, #66bb6a 90%)",
                                                            boxShadow: "0 10px 30px rgba(67, 160, 71, 0.4)",
                                                            position: "relative",
                                                            overflow: "hidden",
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "-100%",
                                                                width: "100%",
                                                                height: "100%",
                                                                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                                                                transition: "left 0.6s",
                                                            },
                                                            "&:hover": {
                                                                transform: "translateY(-4px)",
                                                                boxShadow: "0 15px 40px rgba(67, 160, 71, 0.6)",
                                                                "&::before": {
                                                                    left: "100%"
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        🎯 Start Your Eco Journey - Sign Up Free!
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        component={RouterLink}
                                                        to="/login"
                                                        fullWidth
                                                        sx={{
                                                            py: 2.5,
                                                            fontSize: "1.1rem",
                                                            fontWeight: 800,
                                                            borderRadius: 6,
                                                            borderColor: "#ff9800",
                                                            color: "#ff9800",
                                                            borderWidth: 3,
                                                            position: "relative",
                                                            overflow: "hidden",
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "-100%",
                                                                width: "100%",
                                                                height: "100%",
                                                                background: "linear-gradient(90deg, transparent, rgba(255,152,0,0.1), transparent)",
                                                                transition: "left 0.6s",
                                                            },
                                                            "&:hover": {
                                                                bgcolor: "rgba(255, 152, 0, 0.1)",
                                                                borderWidth: 3,
                                                                transform: "translateY(-2px)",
                                                                "&::before": {
                                                                    left: "100%"
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        🔐 Already Have an Account? Welcome Back!
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Fade>

                        </Stack>
                    </Grid>
                </Grid>

                {/* Enhanced Feedback Dialog */}
                <FeedbackDialog
                    open={feedbackOpen}
                    onClose={() => setFeedbackOpen(false)}
                    classificationResult={result}
                    binColor={binColor}
                    onFeedbackSubmitted={() => {
                        setFeedbackMsg("✅ Thank you for your valuable feedback! Your contribution helps make our AI smarter.");
                        // Optionally refresh user stats here
                    }}
                />

                {/* Impact Stats Section - Show for unauthenticated users */}
                {!isAuthenticated && (
                    <Box sx={{ mt: 6 }}>
                        <ImpactStats variant="section" />
                    </Box>
                )}

                {/* Login Prompt Dialog */}
                <Dialog
                    open={showLoginPrompt}
                    onClose={() => setShowLoginPrompt(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 6,
                            background: "linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)"
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 2, textAlign: "center" }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #ff9800 0%, #ffa726 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 2,
                                boxShadow: "0 6px 25px rgba(255, 152, 0, 0.4)"
                            }}
                        >
                            <Typography fontSize="2.5rem">🔑</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight={800} color="#ff9800" mb={1}>
                            Login Required
                        </Typography>
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            To give feedback and help improve our AI, please sign in first
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 0 }}>
                        <Stack spacing={2}>
                            <Button
                                variant="contained"
                                component={RouterLink}
                                to="/login"
                                fullWidth
                                onClick={() => setShowLoginPrompt(false)}
                                sx={{
                                    py: 1.5,
                                    fontSize: "1.1rem",
                                    fontWeight: 700,
                                    background: "linear-gradient(45deg, #43a047 30%, #66bb6a 90%)",
                                    boxShadow: "0 6px 20px rgba(67, 160, 71, 0.4)"
                                }}
                            >
                                🔐 Login to Your Account
                            </Button>
                            <Button
                                variant="outlined"
                                component={RouterLink}
                                to="/register"
                                fullWidth
                                onClick={() => setShowLoginPrompt(false)}
                                sx={{
                                    py: 1.5,
                                    fontSize: "1rem",
                                    fontWeight: 700,
                                    borderColor: "#ff9800",
                                    color: "#ff9800",
                                    borderWidth: 2,
                                    "&:hover": {
                                        bgcolor: "rgba(255, 152, 0, 0.1)",
                                        borderWidth: 2
                                    }
                                }}
                            >
                                🎯 Create New Account
                            </Button>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 1 }}>
                        <Button
                            onClick={() => setShowLoginPrompt(false)}
                            sx={{ fontWeight: 600, mx: "auto" }}
                        >
                            Maybe Later
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Feedback Success Message */}
                <Collapse in={!!feedbackMsg}>
                    <Alert
                        severity={feedbackMsg.includes("✅") ? "success" : "error"}
                        sx={{
                            mt: 3,
                            borderRadius: 3,
                            fontWeight: 600
                        }}
                        onClose={() => setFeedbackMsg("")}
                    >
                        {feedbackMsg}
                    </Alert>
                </Collapse>

                {/* Image Zoom Modal */}
                <Modal
                    open={zoomOpen}
                    onClose={handleZoomClose}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    sx={{ zIndex: 1302 }}
                >
                    <Fade in={zoomOpen}>
                        <Box
                            sx={{
                                position: "relative",
                                width: "90%",
                                maxWidth: 600,
                                bgcolor: "background.paper",
                                borderRadius: 2,
                                boxShadow: 24,
                                p: 2,
                                mx: "auto",
                                my: 4,
                                overflow: "hidden"
                            }}
                        >
                            <IconButton
                                onClick={handleZoomClose}
                                sx={{
                                    position: "absolute",
                                    top: 16,
                                    right: 16,
                                    color: "white",
                                    bgcolor: "rgba(0, 0, 0, 0.6)",
                                    "&:hover": {
                                        bgcolor: "rgba(0, 0, 0, 0.8)"
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Box
                                component="img"
                                src={zoomImage}
                                alt="Zoomed Preview"
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: 1,
                                    cursor: "zoom-out",
                                    transition: "transform 0.3s ease",
                                    "&:hover": {
                                        transform: "scale(1.05)"
                                    }
                                }}
                            />
                        </Box>
                    </Fade>
                </Modal>
            </Container>

            {/* Additional CSS for animations */}
            <style>{`
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0, -15px, 0);
          }
          70% {
            transform: translate3d(0, -7px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }

        @keyframes float-0 {
          0% { transform: translateY(100vh) translateX(0px) rotate(0deg); }
          100% { transform: translateY(-100px) translateX(50px) rotate(360deg); }
        }

        @keyframes float-1 {
          0% { transform: translateY(100vh) translateX(0px) rotate(0deg); }
          100% { transform: translateY(-100px) translateX(-30px) rotate(-360deg); }
        }

        @keyframes float-2 {
          0% { transform: translateY(100vh) translateX(0px) rotate(0deg); }
          100% { transform: translateY(-100px) translateX(20px) rotate(180deg); }
        }

        @keyframes geometric-float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
          }
          25% {
            transform: translateY(-20px) rotate(90deg) scale(1.1);
          }
          50% {
            transform: translateY(-10px) rotate(180deg) scale(0.9);
          }
          75% {
            transform: translateY(-30px) rotate(270deg) scale(1.05);
          }
        }

        @keyframes pulse-eco {
          0%, 100% {
            box-shadow: 0 0 20px rgba(67, 160, 71, 0.3);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 40px rgba(67, 160, 71, 0.6);
            transform: scale(1.02);
          }
        }
      `}</style>
        </Box>
    );
}
